import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { getHubMismatchRedirect } from '../utils/hubRoutes';
import ForceReauth from './ForceReauth';

const ProtectedRoute = ({ children }) => {
    const { currentUser, userClaims, organization, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!currentUser) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (!userClaims?.orgId) {
        return <Navigate to="/hubs" state={{ from: location }} replace />;
    }

    if (!organization?.id) {
        return <ForceReauth />;
    }

    const hubRedirect = getHubMismatchRedirect(organization, 'app');
    if (hubRedirect) {
        return <Navigate to={hubRedirect} replace />;
    }

    const pathSegments = location.pathname.split('/').filter(segment => segment);
    const isAppRoute = pathSegments[0] === 'app';

    if (isAppRoute) {
        const secondSegment = pathSegments[1];
        const isOrgIdNumeric = secondSegment && !isNaN(parseInt(secondSegment, 10));

        if (secondSegment && !isOrgIdNumeric) {
            const orgId = userClaims.orgId;
            const remainingPath = pathSegments.slice(1).join('/');
            const redirectPath = `/app/${orgId}/${remainingPath}${location.search}${location.hash}`;
            return <Navigate to={redirectPath} replace />;
        }

        if (isOrgIdNumeric) {
            const urlOrgId = parseInt(secondSegment, 10);
            const userOrgId = parseInt(userClaims.orgId, 10);

            if (urlOrgId !== userOrgId) {
                const remainingPath = pathSegments.slice(2).join('/');
                const redirectPath = remainingPath
                    ? `/app/${userOrgId}/${remainingPath}${location.search}${location.hash}`
                    : `/app/${userOrgId}${location.search}${location.hash}`;
                return <Navigate to={redirectPath} replace />;
            }
        }
    }

    return children;
};

export default ProtectedRoute;
